import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge, Card, Empty, ErrorBox, Skeleton } from "@/components/ui";
import type { StyleDocument, StyleProfile, StyleScanResult } from "@/types";

export default function StyleLibrary() {
  const qc = useQueryClient();

  const docs = useQuery({
    queryKey: ["style-docs"],
    queryFn: () => api.get<StyleDocument[]>("/style/documents"),
  });
  const profile = useQuery({
    queryKey: ["style-profile"],
    queryFn: () => api.get<StyleProfile>("/style/profile"),
  });
  const scan = useMutation({
    mutationFn: () => api.post<StyleScanResult>("/style/scan"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["style-docs"] });
      qc.invalidateQueries({ queryKey: ["style-profile"] });
    },
  });
  const rebuild = useMutation({
    mutationFn: () => api.post<StyleProfile>("/style/profile/rebuild"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["style-profile"] }),
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-50">Style Library</h1>
        <div className="text-sm text-slate-400">
          Drop your own consultation notes, follow-up letters, and OTV notes
          into the style dropbox folder. RPIS learns your phrasing and
          structure (formatting only — never clinical evidence).
        </div>
      </div>

      <Card
        title="Library actions"
        actions={
          <div className="flex gap-2">
            <button className="btn-primary" disabled={scan.isPending} onClick={() => scan.mutate()}>
              {scan.isPending ? "Scanning…" : "Scan dropbox"}
            </button>
            <button className="btn-ghost" disabled={rebuild.isPending} onClick={() => rebuild.mutate()}>
              {rebuild.isPending ? "Rebuilding…" : "Rebuild profile"}
            </button>
          </div>
        }
      >
        {scan.data && (
          <div className="text-xs text-slate-300">
            Scan: scanned <b>{scan.data.scanned}</b>, new <b>{scan.data.new_documents}</b>,
            skipped duplicates <b>{scan.data.skipped_duplicates}</b>.
          </div>
        )}
        {scan.error ? <ErrorBox error={scan.error} /> : null}
      </Card>

      <Card title="Style profile">
        {profile.isLoading ? (
          <Skeleton rows={3} />
        ) : !profile.data || profile.data.documents_total === 0 ? (
          <Empty title="No style data yet." hint="Drop a few previous notes/letters into the style dropbox, then scan." />
        ) : (
          <div className="space-y-3">
            <div className="grid md:grid-cols-3 gap-3">
              <div className="card p-3">
                <div className="text-xs uppercase text-slate-400">Documents</div>
                <div className="text-2xl font-bold text-slate-50">{profile.data.documents_total}</div>
              </div>
              <div className="card p-3">
                <div className="text-xs uppercase text-slate-400">Patterns</div>
                <div className="text-2xl font-bold text-slate-50">{profile.data.patterns_total}</div>
              </div>
              <div className="card p-3">
                <div className="text-xs uppercase text-slate-400">Languages</div>
                <div className="text-sm text-slate-200 mt-1 space-x-1">
                  {Object.entries(profile.data.language_distribution).map(([k, v]) => (
                    <Badge key={k} tone="info">{k}: {v}</Badge>
                  ))}
                </div>
              </div>
            </div>
            <div className="card p-3">
              <div className="text-xs uppercase text-slate-400 mb-2">Style type distribution</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(profile.data.style_type_counts).map(([k, v]) => (
                  <Badge key={k} tone="muted">{k}: {v}</Badge>
                ))}
              </div>
            </div>
            {profile.data.common_phrases.length > 0 && (
              <div className="card p-3">
                <div className="text-xs uppercase text-slate-400 mb-2">Common phrases</div>
                <ul className="list-disc pl-5 text-sm text-slate-200">
                  {profile.data.common_phrases.slice(0, 15).map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card title="Documents">
        {docs.isLoading ? (
          <Skeleton rows={3} />
        ) : (docs.data || []).length === 0 ? (
          <Empty title="No style documents yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-400">
                <tr>
                  <th className="text-left p-2">File</th>
                  <th className="text-left p-2">Style type</th>
                  <th className="text-left p-2">Language</th>
                  <th className="text-left p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(docs.data || []).map((d) => (
                  <tr key={d.id} className="border-t border-line/50">
                    <td className="p-2 text-slate-100">{d.original_filename}</td>
                    <td className="p-2">{d.style_type}</td>
                    <td className="p-2">{d.language}</td>
                    <td className="p-2">
                      <Badge tone={d.classification_status === "needs_review" ? "warn" : "info"}>
                        {d.classification_status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
